<script setup>
import { computed, inject } from 'vue';
import { initials, shortDate, styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// Provided by Show.vue when a post template renders a real post; null
// in the editor canvas, where the slot still has to read as itself.
const post = inject('tiqCurrentPost', null);
// A visitor never meets the slot's placeholder: with no real post
// to render it draws nothing, and only the editor keeps the stand-in.
const editing = inject('tiqEditorCanvas', false);

// The design opens an article with the back link, so it lives here
// rather than on post_title: post_hero is the block the starter post
// template ships with, and it is the one at the top of the page.
const heroImage = computed(() => post?.hero_image || props.config.placeholder_image || '');
const title = computed(() => post?.title || 'Post title');
const publishedAt = computed(() => shortDate(post?.published_at));
const author = computed(() => post?.author_name || null);
const photoHeight = computed(() => `${Number(props.config.height) || 480}px`);
</script>

<template>
    <section v-if="post || editing" :id="config.anchor_id || null" class="th-block th-post-hero" :style="styleVars(config)">
        <div class="th-wrap th-sec">
            <a href="/news" class="th-back th-post-hero__back">← All news</a>

            <div class="th-post-hero__head">
                <p v-if="publishedAt" class="th-eyebrow th-post-hero__date">{{ publishedAt }}</p>
                <h1 class="th-h1 th-h1--article">{{ title }}</h1>
                <div v-if="author" class="th-post-hero__author">
                    <span class="th-post-hero__avatar">{{ initials(author) }}</span>
                    <span class="th-post-hero__by">{{ author }}</span>
                </div>
            </div>

            <div class="th-media th-post-hero__photo" :style="{ height: photoHeight }">
                <img v-if="heroImage" :src="heroImage" alt="">
                <span v-else class="th-media__label">Article lead photo</span>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-post-hero__back { display: inline-block; }
.th-post-hero__head { max-width: 820px; margin: 0 auto; }
.th-post-hero__author {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 0;
    border-top: 1px solid var(--th-line);
    border-bottom: 1px solid var(--th-line);
}
.th-post-hero__avatar {
    width: 40px;
    height: 40px;
    flex: none;
    border-radius: 50%;
    background: var(--tint);
    color: var(--accent);
    display: grid;
    place-items: center;
    font: 700 13px/1 var(--th-display);
}
.th-post-hero__by { font-weight: 600; font-size: 15px; }
.th-post-hero__photo { margin-top: clamp(28px, 4vw, 48px); }
</style>
